<template>
  <div>
    <EditorDeckSlidesThreeDLayerListItem
      class="group h-9"
      :selected-in-panel="isSelected(hole as SelectableLayer)"
      :visible="hole.visible"
      @click.left="handleClick($event, hole)"
      @contextmenu.stop.prevent
      @set-visible="handleSetVisible($event, hole)"
    >
      <div class="flex items-center flex-1 w-full truncate layer-title">
        <div v-if="hasCSVFiles" class="pr-2 cursor-pointer" @click="toggleCollapsed">
          <FontAwesomeIcon class="close-icon transition duration-200" icon="caret-down" :class="{ 'transform -rotate-90': collapsed }" />
        </div>
        <MapIcon v-if="hole.type === 'legend'" class="mr-2 text-copper-200" />
        <ThreeDIcon v-else class="w-3 h-3 mr-2 text-copper-200" />
        <span :title="hole.title" class="truncate max-w-4/5">{{ hole.title }}</span>
      </div>
    </EditorDeckSlidesThreeDLayerListItem>

    <slot />
  </div>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue'
import { ref } from 'vue'

import ThreeDIcon from '@/components/icons/ThreeDSlideIcon.svg'
import MapIcon from '@/components/icons/MapIcon.svg'
import type { DrillLabelLayer, DrillLayer } from '~/entities/drillLabel'

type SelectableLayer = { id: string; title: string }

const props = defineProps({
  hole: {
    type: Object as PropType<DrillLayer>,
    required: true,
  },
  hasCSVFiles: {
    type: Boolean,
    default: false,
  },
  selectedLayers: {
    type: Array as PropType<DrillLabelLayer[]>,
    required: true,
  },
})

const emit = defineEmits(['set-layer-visible', 'toggle-collapsed', 'click'])

const collapsed = ref(false)

function toggleCollapsed() {
  collapsed.value = !collapsed.value
  emit('toggle-collapsed', props.hole.title)
}

function handleSetVisible(visible: boolean, layer: DrillLayer) {
  emit('set-layer-visible', visible, layer)
}

function handleClick(e: MouseEvent, hole: DrillLayer) {
  emit('click', e, hole)
}

function isSelected(layer: SelectableLayer) {
  return props.selectedLayers.some(l => l.id === layer.id && 'files' in l)
}
</script>

<style lang="stylus" scoped>
.layer-title
  @apply cursor-default select-none m-0 ml-6
  // These 4 for for no text wrap and ellipsis overflow
  @apply min-w-0 whitespace-nowrap overflow-hidden overflow-ellipsis

.layer.collapsed
  .layer-labels
    @apply max-h-0 overflow-hidden
  .close-icon
    @apply transform -rotate-90

:deep(.controls-container .control-button)
  padding calc(0.75rem - 1px) // Modify padding to have button size match slide buttons
  @apply border-0
</style>
